<template>
  <div class="folder_container">
    <div v-if="fetchingWorkflow" class="folder_header">
      <div v-for="i in 3" :key="`l-${i}`" class="header_item">
        <div class="item_title">
          <v-skeleton-loader class="skeleton" type="text" />
          <v-btn color="grey" class="action" icon disabled>
            <BaseIcon icon="$mdiDotsHorizontal" />
          </v-btn>
        </div>
        <div class="sub_title">
          <v-skeleton-loader class="skeleton" type="text" />
        </div>
        <div class="item_content">
          <v-skeleton-loader class="skeleton" type="text" />
        </div>
        <div v-if="i == 1" class="item_content">
          <v-skeleton-loader class="skeleton" type="text" />
        </div>
      </div>
    </div>
    <template v-if="fetchingWorkflow">
      <div v-if="folder && folder.state == 'CLOSED'" class="archived_alert">
        <div class="archived_alert_icon">
          <BaseIcon icon="$mdiAlertCircle" />
        </div>
        <div class="archived_alert_content">
          <div
            class="archived_info"
            v-text="$t('utils.folderArchivedMessage')"
          />
          <div class="archived_info">
            <div v-text="`${$t('utils.archiveDate')} :`" />
            <div
              class="text_strong"
              v-text="`le ${$d(new Date(folder.dateUpdated), 'veryshort')}`"
            />
          </div>
        </div>
      </div>
    </template>

    <!-- HEADER -->
    <div v-if="folder && !fetchingWorkflow" class="folder_header">
      <!-- FOLDER -->
      <div class="header_item">
        <div class="item_title">
          <span v-text="$t('utils.folder')" />
          <!-- FIRST NESTED MENU -->
          <v-menu v-model="nestedMenuModel.first" offset-y left>
            <template #activator="{ on, attrs }">
              <v-btn color="grey" class="action" icon v-bind="attrs" v-on="on">
                <BaseIcon icon="$mdiDotsHorizontal" />
              </v-btn>
            </template>
            <div class="menu_list">
              <v-btn
                x-large
                class="menu_list_item"
                block
                text
                @click="
                  $store.commit('folder/SET_MODAL_FOlDER_EDIT_NAME', true)
                "
              >
                <BaseIcon class="icon" icon="$mdiPencilOutline" small left />
                <span class="label" v-text="$t('folder.editName')" />
              </v-btn>
              <v-btn
                x-large
                class="menu_list_item"
                block
                text
                @click="$store.commit('folder/setFolderLogsDialogOpen', true)"
              >
                <BaseIcon class="icon" icon="$mdiClockOutline" small left />
                <span class="label" v-text="$t('folder.seeHistory')" />
              </v-btn>
              <!-- SECOND NESTED MENU -->
              <template v-if="exportCheck">
                <v-menu
                  v-model="nestedMenuModel.second"
                  :close-on-content-click="false"
                  :close-on-content-leave="false"
                  open-on-hover
                  offset-x
                  right
                >
                  <template #activator="{ on, attrs }">
                    <v-btn
                      x-large
                      class="menu_list_item"
                      block
                      text
                      v-bind="attrs"
                      v-on="on"
                      @click.stop.prevent
                    >
                      <BaseIcon class="icon" icon="$mdiDownload" small left />
                      <span class="label" v-text="$t('utils.download')" />
                    </v-btn>
                  </template>
                  <div class="menu_list">
                    <v-btn
                      x-large
                      class="menu_list_item"
                      block
                      text
                      :disabled="!exportCheck.pdf.allowed"
                      :loading="exportingType === 'PDF'"
                      @click="downloadFolder('PDF')"
                    >
                      <BaseIcon
                        class="icon"
                        icon="$mdiFileOutline"
                        small
                        left
                      />
                      <span class="label" v-text="'PDF'" />
                    </v-btn>
                    <v-btn
                      x-large
                      class="menu_list_item"
                      block
                      text
                      :disabled="disabledExportZipBtn"
                      :loading="exportingType === 'ZIP'"
                      @click="downloadFolder('ZIP')"
                    >
                      <BaseIcon class="icon" icon="$mdiFolderZip" small left />
                      <span class="label" v-text="'Archive'" />
                    </v-btn>
                  </div>
                </v-menu>
              </template>
              <v-btn
                v-if="folder.state == 'OPEN'"
                x-large
                class="menu_list_item"
                block
                text
                @click="archiveFolder()"
              >
                <BaseIcon class="icon" icon="$mdiArchive" small left />
                <span class="label" v-text="$t('utils.archive')" />
              </v-btn>
              <v-btn
                x-large
                class="menu_list_item"
                block
                text
                @click="$store.commit('folder/SET_MODAL_DELETE_FOLDER', true)"
              >
                <BaseIcon class="icon" icon="$mdiTrashCan" small left />
                <span class="label" v-text="$t('folder.deleteFolder')" />
              </v-btn>
            </div>
          </v-menu>
        </div>
        <div class="d-flex flex-row">
          <div
            class="pastille"
            :style="{ backgroundColor: getValidationColor }"
          />
          <div class="sub_title" v-text="folder.name" />
        </div>

        <div class="item_content">
          <div class="item_key" v-text="`${$t('utils.creation')} : `" />
          <div
            class="item_value"
            v-text="$d(new Date(folder.dateCreated), 'veryshort')"
          />
        </div>
        <div class="item_content">
          <div class="item_key" v-text="`${$t('utils.lastEdit')} : `" />
          <div
            class="item_value"
            v-text="$d(new Date(folder.dateUpdated), 'veryshort')"
          />
        </div>
        <button
          v-if="showDownloadZipBtn"
          class="download_zip_btn"
          :disabled="disabledDownloadZipBtn"
          :class="{
            custom_tooltip: disabledDownloadZipBtn,
          }"
          @click="downloadZIPFile(exportCheck.lastZipExport)"
        >
          <BaseIcon class="icon" icon="$mdiDownload" />
          <span
            v-text="
              $t('utils.download_archive_generated', {
                date: $d(
                  new Date(exportCheck.lastZipExport.dateCreated),
                  'veryshort'
                ),
              })
            "
          />
          <div
            v-if="disabledDownloadZipBtn"
            class="tooltip_content"
            v-text="
              $t(
                exportInError
                  ? 'folder.downloadFolderError'
                  : 'folder.downloadFolderInProgress'
              )
            "
          />
        </button>
        <template v-if="folder.state == 'CLOSED'">
          <div class="item_content_right">
            <button
              class="unarchive_btn"
              :disabled="updatingFolderState"
              @click="unarchiveFolder()"
            >
              <BaseIcon class="icon" icon="$mdiFolderRefreshOutline" />
              <span v-text="$t('utils.unarchive')" />
            </button>
          </div>
          <div
            v-if="remainingDeletionDays"
            class="alert_message"
            v-text="remainingDeletionDays"
          />
        </template>
        <div v-else class="item_footer_action">
          <button
            v-if="!watchers.items.length"
            class="footer_btn"
            @click="openAssignWatcherDialog = true"
          >
            <span v-text="$t('utils.add_observer')" />
          </button>
          <button
            v-else
            class="followers"
            @click="openAssignWatcherDialog = true"
          >
            <div
              v-for="(user, index) in watchers.items.slice(0, 3)"
              :key="`u${index}`"
              class="avatar_container"
            >
              <div v-text="user.firstChar" />
            </div>
            <div v-if="watchers.items.length > 3" class="dots" v-text="'...'" />
          </button>
          <button
            :disabled="submittingFollower"
            class="footer_btn"
            @click="handleFollow()"
          >
            <BaseIcon class="icon" :icon="watchingBtn.icon" />
            <span v-text="watchingBtn.label" />
          </button>
        </div>
        <BaseButton
          v-if="folder.collectionInProgress"
          class="relaunch_btn"
          block
          @click="displayRelaunchFolderDialog = true"
        >
          <span v-text="$t('utils.relaunch')" />
        </BaseButton>
      </div>
      <!-- CONTACT -->
      <div class="header_item">
        <div class="item_title">
          <span v-text="$tc('utils.contact', 1)" />
          <v-menu v-if="folder.client && hasClient" offset-y left>
            <template #activator="{ on, attrs }">
              <v-btn color="grey" class="action" icon v-bind="attrs" v-on="on">
                <BaseIcon icon="$mdiDotsHorizontal" />
              </v-btn>
            </template>
            <div class="menu_list">
              <v-btn
                x-large
                class="menu_list_item"
                block
                text
                @click="
                  $router.push({
                    name: 'clientDetail',
                    params: {
                      organizationId: $route.params.organizationId,
                      clientId: folder.client.id,
                    },
                  })
                "
              >
                <BaseIcon class="icon" icon="$mdiAccount" small left />
                <span class="label" v-text="$t('utils.seeContactDetails')" />
              </v-btn>
              <v-btn
                x-large
                class="menu_list_item"
                block
                text
                @click="$store.commit('folder/setFolderChangeClient', true)"
              >
                <BaseIcon class="icon" icon="$mdiPencilOutline" small left />
                <span class="label" v-text="$t('folder.editClient')" />
              </v-btn>
            </div>
          </v-menu>
        </div>
        <template v-if="folder.client && hasClient">
          <div class="sub_title" v-text="folder.client.email" />
          <div v-if="folder.client.fullName" class="item_content">
            <div class="item_key" v-text="`${$t('utils.fullname')} : `" />
            <div class="item_value" v-text="folder.client.fullName" />
          </div>
        </template>
        <template v-else>
          <div class="sub_title" v-text="$t('utils.noAssignedContact')" />
          <div v-if="folder.state == 'OPEN'" class="item_footer_action">
            <button
              class="footer_btn"
              @click="$store.commit('folder/setFolderChangeClient', true)"
            >
              <BaseIcon class="icon" icon="$mdiAccount" />
              <span v-text="$t('utils.assignedContact')" />
            </button>
          </div>
        </template>
      </div>
      <!-- WORKFLOW -->
      <div class="header_item">
        <div class="item_title">
          <span v-text="$t('utils.workflow')" />
          <v-menu offset-y left>
            <template #activator="{ on, attrs }">
              <v-btn color="grey" class="action" icon v-bind="attrs" v-on="on">
                <BaseIcon icon="$mdiDotsHorizontal" />
              </v-btn>
            </template>
            <div class="menu_list">
              <v-btn
                x-large
                class="menu_list_item"
                block
                text
                @click="
                  $store.commit('folder/SET_MODAL_FOLDER_EDIT_WORKFLOW', true)
                "
              >
                <BaseIcon class="icon" icon="$mdiPencilOutline" small left />
                <span class="label" v-text="$t('folder.editWorkflow')" />
              </v-btn>
            </div>
          </v-menu>
        </div>
        <template v-if="activeWorkflow">
          <div class="sub_title" v-text="activeWorkflow.name" />
          <div v-if="folder.assign" class="item_content">
            <div class="item_key" v-text="$t('utils.assignedCollaborator')" />
            <div class="item_value" v-text="assignEmail" />
          </div>
          <div class="item_footer_action">
            <button
              v-if="folder.state == 'OPEN'"
              class="footer_btn"
              @click="
                $store.commit(
                  'folder/SET_MODAL_ASSIGN_COLLABORATOR_STATUS',
                  true
                )
              "
            >
              <BaseIcon class="icon" icon="$mdiAccount" />
              <span v-text="$t('utils.editAssignedCollaborator')" />
            </button>
          </div>
        </template>
        <template v-else>
          <div class="sub_title" v-text="$t('utils.noAssignedWorkflow')" />
          <div v-if="folder.state == 'OPEN'" class="item_footer_action">
            <button
              class="footer_btn"
              @click="
                $store.commit('folder/SET_MODAL_FOLDER_EDIT_WORKFLOW', true)
              "
            >
              <BaseIcon class="icon" icon="$mdiCogs" />
              <span v-text="$t('utils.assignWorkflow')" />
            </button>
          </div>
        </template>
      </div>
    </div>

    <!-- WORKFLOW -->
    <div class="folder_body">
      <FolderWokflowDetail
        v-if="activeWorkflow"
        :workflow="activeWorkflow"
        :folder-active-status-id="folder ? folder.status : NaN"
        @questionAnswered="fetchFolderExportCheck()"
      />
      <FolderRecords
        v-if="folder"
        :organization-id="organizationId"
        :enable-relaunch="enableRelaunch"
        :folder-id="folderId"
      />
    </div>

    <!-- DIALOG -->
    <FolderEditNameDialog />
    <FolderLogsShowDialog />
    <FolderChangeOwnerDialog />
    <FolderAssignCollaboratorDialog />
    <FolderEditWorkflowDialog />
    <FolderSendDataToDPAEDialog />
    <FolderSendMailDialog />
    <FolderDeleteDialog />
    <FolderAssignWatcherDialog
      v-if="openAssignWatcherDialog"
      :organization-id="organizationId * 1"
      :folder-id="folderId * 1"
      :watchers="watchers.items"
      @close="openAssignWatcherDialog = false"
      @updateWatchers="updateWatchers"
    />
    <FolderRelaunchDialog
      v-if="displayRelaunchFolderDialog"
      @close="displayRelaunchFolderDialog = false"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import User from "@/core/Models/User";
import FolderAPI from "@/modules/Folder/Services/folder.api";
import {
  getFolderExportCheck,
  postFolderExport,
} from "@/modules/Folder/Services/folder.service";
import { EXPORT_FILE_TYPE, EXPORT_STATUS } from "@/core/Models/FolderExport";
import FolderAssignWatcherDialog from "@/modules/Folder/Components/FolderAssignWatcherDialog.vue";
import FolderWokflowDetail from "@/modules/Folder/Components/FolderWokflowDetail";
import FolderEditNameDialog from "@/modules/Folder/Components/FolderEditNameDialog.vue";
import FolderLogsShowDialog from "@/modules/Folder/Components/FolderLogsShowDialog.vue";
import FolderChangeOwnerDialog from "@/modules/Folder/Components/FolderChangeOwnerDialog.vue";
import FolderAssignCollaboratorDialog from "@/modules/Folder/Components/FolderAssignCollaboratorDialog.vue";
import FolderEditWorkflowDialog from "@/modules/Folder/Components/FolderEditWorkflowDialog.vue";
import FolderRecords from "@/modules/Folder/Components/FolderRecords.vue";
import FolderSendDataToDPAEDialog from "../Components/FolderSendDataToDPAEDialog.vue";
import FolderSendMailDialog from "../Components/FolderSendMailDialog.vue";
import FolderDeleteDialog from "../Components/FolderDeleteDialog.vue";
import FolderRelaunchDialog from "@/modules/Folder/Components/FolderRelaunchDialog.vue";
import { saveAs } from "file-saver";

export default {
  name: "FolderDetail",
  components: {
    FolderWokflowDetail,
    FolderEditNameDialog,
    FolderLogsShowDialog,
    FolderChangeOwnerDialog,
    FolderAssignCollaboratorDialog,
    FolderEditWorkflowDialog,
    FolderRecords,
    FolderSendDataToDPAEDialog,
    FolderSendMailDialog,
    FolderDeleteDialog,
    FolderAssignWatcherDialog,
    FolderRelaunchDialog,
  },
  props: {
    organizationId: {
      type: [Number, String],
      required: true,
    },
    folderId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      updatingFolderState: false,
      fetchingWorkflow: true,
      submittingFollower: false,
      openAssignWatcherDialog: false,
      exportCheck: null,
      exportingType: null,
      displayRelaunchFolderDialog: false,
      nestedMenuModel: {
        first: false,
        second: false,
      },
      watchers: {
        items: [],
        loading: true,
      },
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.account.user,
      folder: (state) => state.folder.activeFolder,
      activeWorkflow: (state) => state.workflow.activeWorkflow,
    }),
    workflowId() {
      return this.folder ? this.folder.workflow : null;
    },
    assignEmail() {
      return this.folder?.assign ? this.folder?.assign?.email : "";
    },
    getValidationColor() {
      switch (this.folder.validationStatus) {
        case "UPDATE_REQUESTED":
          return "#ff5267";
        case "UPDATED":
          return "#ffab5a";
        case "VALIDATED":
          return "#18a300";
        default:
          return this.folder.hasSpamEmail ? "#FF6B00" : "#b6bdff";
      }
    },
    hasClient() {
      return !this.folder?.client?.isTemporary;
    },
    userIsWatching() {
      return this.watchers.items.some((item) => item.id === this.user.id);
    },
    watchingBtn() {
      if (this.userIsWatching)
        return {
          icon: "$mdiEyeOff",
          label: this.$t("utils.unfollow"),
        };
      return {
        icon: "$mdiEye",
        label: this.$t("utils.follow"),
      };
    },
    showDownloadZipBtn() {
      if (!this.exportCheck?.lastZipExport) return false;
      const now = new Date();
      const twoDaysInMs = 2 * 24 * 60 * 60 * 1000;
      const diffInMs = now - this.exportCheck.lastZipExport.dateCreated;
      return diffInMs <= twoDaysInMs;
    },
    disabledDownloadZipBtn() {
      return (
        !this.exportCheck?.lastZipExport ||
        this.exportCheck.lastZipExport.status !== EXPORT_STATUS.SUCCESS
      );
    },
    exportInError() {
      return (
        !this.exportCheck?.lastZipExport ||
        this.exportCheck.lastZipExport.status === EXPORT_STATUS.FAILED
      );
    },
    disabledExportZipBtn() {
      if (!this.exportCheck?.lastZipExport) return false;
      return (
        this.exportCheck.lastZipExport.status === EXPORT_STATUS.IN_PROGRESS
      );
    },
    enableRelaunch() {
      if (!this.activeWorkflow) return true;
      return this.activeWorkflow.sendCollectionEmails;
    },
    remainingDeletionDays() {
      if (!this.folder || isNaN(this.folder.remainingDeletionDays)) return "";
      if (this.folder.remainingDeletionDays == 0)
        return this.$t("workflow_delete_folder.folder_alert_2");
      if (this.folder.remainingDeletionDays > 0)
        return this.$tc(
          "workflow_delete_folder.folder_alert",
          this.folder.remainingDeletionDays,
          {
            days: this.folder.remainingDeletionDays,
          }
        );
      return "";
    },
  },
  watch: {
    folderId: {
      async handler(newValue, oldValue) {
        if (newValue != oldValue) {
          this.loadStoreData();
        }
      },
      immediate: true,
    },
    folder: {
      handler(newVal, oldVal) {
        if ((!oldVal && newVal) || (newVal && newVal.name !== oldVal.name)) {
          this.setBreadcrumbLabel();
        }
      },
      immediate: true,
    },
    workflowId: {
      handler(newValue, oldValue) {
        if (newValue != oldValue) {
          this.fetchWorkflow();
        }
      },
      immediate: true,
    },
  },
  created() {
    this.fetchFolderWatcher();
    this.fetchFolderExportCheck();
  },
  destroyed() {
    this.$store.commit("workflow/SET_ACTIVE_WORKFLOW", null);
    this.$store.commit("folder/SET_ACTIVE_FOLDER", null);
  },
  methods: {
    async loadStoreData() {
      if (!this.organizationId || !this.folderId) return;
      try {
        await this.$store.dispatch("folder/fetchActiveFolder", {
          organizationId: this.organizationId,
          folderId: this.folderId,
        });
        await this.$store.dispatch("folder/fetchClientOwnfolder", {
          organizationId: this.organizationId,
          folderId: this.folderId,
        });
      } catch {
        this.$router.push({
          name: "folderList",
          params: {
            organizationId: this.organizationId,
          },
        });
      }
    },
    async fetchWorkflow() {
      this.fetchingWorkflow = true;
      this.$store.commit("workflow/SET_ACTIVE_WORKFLOW", null);
      if (this.workflowId) {
        await this.$store.dispatch("workflow/fetchActiveWorkflow", {
          organizationId: this.organizationId,
          workflowId: this.workflowId,
        });
      }
      this.fetchingWorkflow = false;
    },
    setBreadcrumbLabel() {
      this.$route.meta.breadcrumbs[1].label = `${this.folder.name} `;
    },
    async fetchFolderWatcher() {
      this.watchers.loading = true;
      try {
        const res = await FolderAPI.getFolderWatcher({
          organizationId: this.organizationId,
          folderId: this.folderId,
        });
        this.watchers.items = res.data.results.map(
          (item) => new User({ ...item })
        );
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.watchers.loading = false;
    },
    async handleFollow() {
      this.submittingFollower = true;
      try {
        const params = {
          organizationId: this.organizationId,
          folderId: this.folderId,
          payload: {
            collaborators: [this.user.id],
          },
        };
        if (this.userIsWatching) {
          await FolderAPI.postFolderWatcherUnfollow(params);
          this.watchers.items = this.watchers.items.filter(
            (item) => item.id !== this.user.id
          );
        } else {
          await FolderAPI.postFolderWatcherFollow(params);
          this.watchers.items.push(new User({ ...this.user }));
        }
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.submittingFollower = false;
    },
    updateWatchers(items) {
      this.watchers.items = items;
    },
    async fetchFolderExportCheck() {
      try {
        this.exportCheck = await getFolderExportCheck({
          organizationId: this.organizationId,
          folderId: this.folderId,
        });
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },

    async downloadFolder(fileType) {
      this.exportingType = fileType;
      try {
        const folderExport = await postFolderExport({
          organizationId: this.$route.params.organizationId,
          folderId: this.$route.params.folderId,
          payload: {
            export_file_type: fileType,
          },
        });
        if (folderExport.exportFileType == EXPORT_FILE_TYPE.PDF) {
          saveAs(folderExport.exportFile);
        } else {
          this.exportCheck.lastZipExport = folderExport;
          this.$store.dispatch("snackbar/active", {
            message: this.$t("folder.downloadFolderInProgress"),
            type: "SUCCESS",
          });
        }
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.oopsError"),
          type: "ERROR",
        });
      }
      this.exportingType = null;
      this.nestedMenuModel = {
        first: false,
        second: false,
        third: false,
      };
    },
    downloadZIPFile(zipExported) {
      saveAs(zipExported.exportFile);
    },
    async archiveFolder() {
      this.updatingFolderState = true;
      try {
        await this.$store.dispatch("folder/archiveFolder", {
          organizationId: this.$route.params.organizationId,
          folderId: this.$store.state.folder.activeFolder.id,
        });
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.snackbar.folderArchivedSuccessfully"),
          type: "SUCCESS",
        });
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.updatingFolderState = false;
    },
    async unarchiveFolder() {
      this.updatingFolderState = true;
      try {
        await this.$store.dispatch("folder/unarchiveFolder", {
          organizationId: this.$route.params.organizationId,
          folderId: this.$store.state.folder.activeFolder.id,
        });
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.snackbar.folderUnarchivedSuccessfully"),
          type: "SUCCESS",
        });
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.updatingFolderState = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.folder_container {
  position: relative;
  .archived_alert {
    padding: 16px;
    border-radius: 5px;
    background: #f7e7da;
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    position: sticky;
    top: -32px;
    z-index: 100;
    .archived_alert_icon {
      &::v-deep {
        .v-icon {
          color: #ff6b00;
        }
      }
    }
    .archived_alert_content {
      padding: 0 16px;
      .archived_info {
        display: flex;
        align-items: center;
        font-family: "Inter";
        font-style: normal;
        font-weight: 400;
        font-size: 12px;
        line-height: 18px;
        color: #ff6b00;
        .text_strong {
          font-weight: 600;
          margin-left: 6px;
        }
      }
    }
  }
  .folder_header {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 24px;
    margin-bottom: 24px;
    .header_item {
      display: flex;
      flex-direction: column;
      border-radius: 5px;
      flex: 1;
      padding: 24px;
      background: #ffffff;
      border: 0.5px solid #ecebf1;
      position: relative;
      width: 100%;
      overflow: hidden;
      word-break: keep-all;
      word-wrap: break-word;
      .pastille {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        padding: 8px;
        margin-right: 10px;
        position: relative;
        top: 5px;
      }
      .item_title {
        display: flex;
        align-items: center;
        font-family: "Inter";
        font-style: normal;
        font-weight: 400;
        font-size: 12px;
        line-height: 16px;
        text-transform: uppercase;
        color: #707080;
        margin-bottom: 0.3em;
        .action {
          width: 24px;
          height: 24px;
          margin-left: auto;
        }
        .skeleton {
          height: 16px;
          width: 70px;
          ::v-deep {
            .v-skeleton-loader__bone {
              height: 12px;
            }
          }
        }
      }
      .sub_title {
        font-family: "Inter";
        font-style: normal;
        font-weight: 600;
        font-size: 24px;
        line-height: 32px;
        color: #353542;
        margin-bottom: 0.6em;
        .skeleton {
          height: 32px;
          width: 66%;
          ::v-deep {
            .v-skeleton-loader__bone {
              height: 24px;
            }
          }
        }
      }
      .item_content {
        display: flex;
        margin-top: 0.6em;
        .item_key {
          margin-right: 6px;
          font-family: "Inter";
          font-style: normal;
          font-weight: 400;
          font-size: 12px;
          line-height: 16px;
          color: #707080;
        }
        .item_value {
          font-family: "Inter";
          font-style: normal;
          font-weight: 600;
          font-size: 12px;
          line-height: 16px;
          color: #707080;
        }
        .skeleton {
          height: 16px;
          width: 50%;
          ::v-deep {
            .v-skeleton-loader__bone {
              height: 12px;
            }
          }
        }
      }
      .alert_message {
        margin-top: 6px;
        font-size: 12px;
        line-height: 16px;
        color: red;
      }
      .item_footer_action {
        align-self: flex-end;
        justify-self: flex-end;
        padding-top: 12px;
        margin-top: auto;
        gap: 8px;
        display: flex;
        align-items: center;
        .footer_btn {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 8px 12px;
          background: #f4f7ff;
          border-radius: 5px;
          font-family: "Inter";
          font-style: normal;
          font-weight: 500;
          font-size: 14px;
          line-height: 16px;
          color: #2536cc;
          & > .icon {
            &::v-deep {
              .v-icon {
                width: 16px;
                height: 16px;
                color: #2536cc;
              }
            }
          }
          & > * {
            margin: 0 4px;
          }
        }
      }
      .relaunch_btn {
        margin-top: 16px;
      }
      .followers {
        display: flex;
        align-items: center;
        padding-left: 10px;
        cursor: pointer;
        .avatar_container {
          border: solid 1px #fff;
          margin-left: -10px;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #e5e5e5;
          border-radius: 50%;
          font-family: "Inter";
          font-size: 16px;
          line-height: 16px;
          color: #353542;
          font-weight: 600;
        }
        .dots {
          margin-left: 4px;
          font-weight: bold;
          line-height: 16px;
          font-size: 20px;
          align-self: flex-end;
          margin-bottom: 4px;
          color: #707080;
        }
      }
      .download_zip_btn {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 8px 12px;
        background: #f7e7da;
        border-radius: 5px;
        font-family: "Inter";
        font-style: normal;
        font-weight: 500;
        font-size: 14px;
        line-height: 16px;
        color: #ff6b00;
        gap: 4px;
        margin-top: 12px;
        & > .icon {
          &::v-deep {
            .v-icon {
              width: 16px;
              height: 16px;
              color: #ff6b00;
            }
          }
        }
        &:disabled {
          background: #f4f7ff;
          color: #c5c5cf;
          cursor: not-allowed;
          & > .icon {
            &::v-deep {
              .v-icon {
                color: #c5c5cf;
              }
            }
          }
        }
      }

      .item_content_right {
        display: flex;
        justify-content: flex-end;
        margin-top: 16px;
        .unarchive_btn {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 8px 12px;
          background: #f7e7da;
          border-radius: 5px;
          font-family: "Inter";
          font-style: normal;
          font-weight: 500;
          font-size: 14px;
          line-height: 16px;
          color: #ff6b00;
          & > .icon {
            &::v-deep {
              .v-icon {
                width: 16px;
                height: 16px;
                color: #ff6b00;
              }
            }
          }
          & > * {
            margin: 0 4px;
          }
        }
      }
    }
  }
  .folder_body {
    .stepper_content {
      display: flex;
      .status_section {
        width: calc(100% - 250px);
        flex: none;
      }
      .action_section {
        width: 250px;
        flex: none;
      }
    }
  }
}
.menu_list {
  background: #fff;
  .menu_list_item {
    display: flex;
    justify-content: flex-start;
    &::v-deep {
      .icon {
        color: #cfcfdc;
      }
      .label {
        font-family: "Inter";
        font-style: normal;
        font-weight: 400;
        font-size: 14px;
        line-height: 16px;
        color: #707080;
        transition: all 0.3s ease-in;
      }
    }
    &:hover {
      &::v-deep {
        .icon {
          color: #2536cc;
        }
        .label {
          color: #2536cc;
        }
      }
    }
    &.v-btn--disabled {
      &::v-deep {
        .icon {
          .v-icon {
            color: #cfcfdc !important;
          }
        }
        .label {
          color: #cfcfdc;
        }
      }
    }
  }
}
.custom_tooltip {
  position: relative;
  .tooltip_content {
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(0, calc(-100% - 20px));
    padding: 12px 16px;
    border-radius: 5px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.25s;
    background-color: #707080;
    z-index: 10;
    width: 200px;
    font-size: 14px;
    line-height: 18px;
    color: #fff;
    text-align: center;
  }
  &::before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(-24px, -21px);
    height: 0;
    width: 0;
    border: 12px solid transparent;
    border-top-color: #707080;
    z-index: 11;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.25s;
  }

  &:hover {
    .tooltip_content {
      opacity: 1;
    }
    &::before {
      opacity: 1;
    }
  }
}
</style>
